import { brandGradient, brandIdentity, readableInk } from '@/lib/brands';
import { CarSilhouette } from '@/components/CarSilhouette';
import { cn } from '@/lib/cn';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZES: Record<Size, { box: string; text: string; radius: string; inset: string }> = {
  xs: { box: 'size-5', text: 'text-[8px]', radius: 'rounded-[6px]', inset: 'p-[2px]' },
  sm: { box: 'size-7', text: 'text-[10px]', radius: 'rounded-[8px]', inset: 'p-[3px]' },
  md: { box: 'size-9', text: 'text-[13px]', radius: 'rounded-[11px]', inset: 'p-1' },
  lg: { box: 'size-12', text: 'text-[17px]', radius: 'rounded-[15px]', inset: 'p-1.5' },
  xl: { box: 'size-16', text: 'text-[23px]', radius: 'rounded-[20px]', inset: 'p-2' },
  '2xl': { box: 'size-20', text: 'text-[29px]', radius: 'rounded-[24px]', inset: 'p-2.5' },
};

/** Three-letter monograms need to shed some width or they touch the badge edge. */
const TRACKING: Record<number, string> = {
  1: 'tracking-normal',
  2: 'tracking-[-0.04em]',
  3: 'tracking-[-0.07em]',
};

interface Props {
  make?: { name: string; slug?: string } | null;
  size?: Size;
  className?: string;
  /** Off for decorative use next to a visible make name, which is almost everywhere. */
  labelled?: boolean;
  /** Skips lazy loading. For the one mark that has to be on screen the instant it mounts. */
  priority?: boolean;
}

/**
 * The little enamel badge that sits beside every make name.
 *
 * Where we have the marque's own logo it goes on a white plate — white in both themes, because
 * nearly every manufacturer mark is drawn for a light background and a good number of them
 * (Cadillac, Jaguar, Chrysler, Toyota) disappear completely on a dark one. Anything we have no
 * artwork for falls back to a monogram in the marque's colour, which is what stops a make added to
 * the catalogue tomorrow from rendering as a hole.
 *
 * The gloss and the inner bevel on the monogram are what make it read as a physical badge instead
 * of a coloured square, and both are pure CSS so a grid of sixty of them costs nothing.
 */
export function BrandMark({
  make,
  size = 'md',
  className,
  labelled = false,
  priority = false,
}: Props) {
  const identity = brandIdentity(make);
  const dimensions = SIZES[size];

  // Caller classes are appended last in both branches: `twMerge` lets the later class win, so a
  // caller passing its own shadow must not be overridden by the plate's.
  const shell = cn(
    'relative isolate inline-flex shrink-0 items-center justify-center overflow-hidden select-none',
    dimensions.box,
    dimensions.radius,
  );

  const semantics = {
    role: labelled ? ('img' as const) : undefined,
    'aria-label': labelled ? make?.name : undefined,
    'aria-hidden': labelled ? undefined : true,
  };

  if (identity.logo) {
    return (
      <span
        className={cn(
          shell,
          'bg-white shadow-[0_1px_2px_rgb(0_0_0/0.18),inset_0_0_0_1px_rgb(0_0_0/0.08)]',
          dimensions.inset,
          className,
        )}
        {...semantics}
      >
        <img
          src={identity.logo}
          alt=""
          draggable={false}
          decoding="async"
          loading={priority ? 'eager' : 'lazy'}
          className="size-full object-contain"
        />
      </span>
    );
  }

  const ink = readableInk(identity.colour);

  return (
    <span
      className={cn(shell, 'shadow-[0_1px_2px_rgb(0_0_0/0.18)]', className)}
      style={{ backgroundImage: brandGradient(identity.colour), color: ink }}
      {...semantics}
    >
      {/* Top-lit bevel: a light hairline inside the top edge, a dark one inside the bottom. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{
          boxShadow:
            'inset 0 1px 0 0 rgb(255 255 255 / 0.28), inset 0 -1px 0 0 rgb(0 0 0 / 0.22), inset 0 0 0 1px rgb(0 0 0 / 0.1)',
        }}
      />

      {/* A single specular highlight across the upper left. This is the whole illusion. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-y-1/2 -left-1/3 w-2/3 -rotate-[28deg] bg-white/20 blur-[2px]"
      />

      {identity.glyph ? (
        <CarSilhouette className="relative w-[62%]" />
      ) : (
        <span
          className={cn(
            'relative font-display font-extrabold',
            dimensions.text,
            TRACKING[identity.monogram.length] ?? 'tracking-[-0.07em]',
          )}
        >
          {identity.monogram}
        </span>
      )}
    </span>
  );
}
